import {
	getPersonStatusText,
	SignerPanelStatusColorMap,
	isConcludedStatus,
	isSignedStatus,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/Documents.utils';
import { getSignerAction } from './SignerCard.utils';
import {
	DocumentSigningRoleType,
	DocumentWorkflowInstanceSignatureStatus,
	type InvestmentProgrammeDocumentDetailsWitnessRes,
} from '@dealstack/shared/lib/generated';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { type ReactNode, useState } from 'react';
import { Flex } from '@mantine/core';
import { formatUserFullName } from '@dealstack/shared/lib/format/user';
import { ConnectedChildren } from '@dealstack/frontend/shared/components/ExpandablePersonCard/ConnectedChildren';
import { SubPersonRow } from '@dealstack/frontend/shared/components/ExpandablePersonCard/SubPersonRow';
import {
	PersonStatus,
	PersonTag,
} from '@dealstack/frontend/shared/components/ExpandablePersonCard/ExpandablePersonCard.types';
import type { GroupMenuItem } from '@dealstack/frontend/shared/components/ExpandablePersonCard/GroupHeader.types';
import { logger } from '@sentry/react';
import { ExpandablePersonCard } from '@dealstack/frontend/shared/components/ExpandablePersonCard/ExpandablePersonCard';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	firstName?: string | null;
	lastName?: string | null;
	email?: string | null;
	status?: DocumentWorkflowInstanceSignatureStatus;
	tags?: PersonTag[];
	subtitle?: string | null;
	subtitleColor?: string;
	witnesses?: InvestmentProgrammeDocumentDetailsWitnessRes[];
	showSignButton?: boolean;
	signDisabled?: boolean;
	signDisabledMessage?: string;
	onSignClick?: () => void;
	showWitnessButton?: boolean;
	witnessButtonLabel?: string;
	onWitnessClick?: () => void;
	showResendButton?: boolean;
	resendButtonLabel?: string;
	onResendClick?: () => void;
	isResending?: boolean;
	onResendWitnessInvitation?: (signatureId: string) => void;
	resendingSignatureId?: string | null;
	claimMenuItems?: GroupMenuItem[];
	claimedByName?: string | null;
	defaultExpanded?: boolean;
	menuItems?: GroupMenuItem[];
	menuTestId?: string;
	getWitnessMenuItems?: (
		witness: InvestmentProgrammeDocumentDetailsWitnessRes
	) => GroupMenuItem[];
	children?: ReactNode;
}

export const SignerCard = ({
	firstName,
	lastName,
	email,
	status,
	tags,
	subtitle,
	subtitleColor,
	witnesses,
	showSignButton,
	signDisabled,
	signDisabledMessage,
	onSignClick,
	showWitnessButton,
	witnessButtonLabel,
	onWitnessClick,
	showResendButton,
	resendButtonLabel,
	onResendClick,
	isResending,
	onResendWitnessInvitation,
	resendingSignatureId,
	claimMenuItems,
	claimedByName,
	defaultExpanded = false,
	menuItems,
	menuTestId,
	getWitnessMenuItems,
	children,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	const displayStatus = status ?? DocumentWorkflowInstanceSignatureStatus.ERROR;
	const fullName = formatUserFullName({ firstName, lastName });

	const personStatus: PersonStatus = {
		label: getPersonStatusText(displayStatus, tMipManage),
		color: SignerPanelStatusColorMap[displayStatus],
	};

	const action = getSignerAction({
		showSignButton,
		onSignClick,
		signButtonLabel: tMipManage('documents_page.signer.sign_button'),
		signDisabled,
		signDisabledMessage,
		showWitnessButton,
		onWitnessClick,
		witnessButtonLabel,
		showResendButton,
		onResendClick,
		resendButtonLabel,
		resendDisabled: isResending,
	});

	const hasWitnesses = witnesses && witnesses.length > 0;
	const hasExpandableContent = hasWitnesses || !!children;

	const [expanded, setExpanded] = useState(defaultExpanded);

	// A claimed signer isn't signed until its signature reaches a signed status —
	// until then it's only claimed, so the subtitle must read "claimed", not
	// "signed on their behalf" (DC-976).
	const claimedOnBehalfKey = isSignedStatus(displayStatus)
		? 'documents_page.signer.signed_on_behalf'
		: 'documents_page.signer.claimed_on_behalf';
	const resolvedSubtitle = claimedByName
		? tMipManage(claimedOnBehalfKey, { name: claimedByName })
		: subtitle;
	const resolvedSubtitleColor = claimedByName ? undefined : subtitleColor;

	return (
		<Flex direction="column" gap="0.25rem" data-testid={TEST_ELEMENT_IDS.DOCUMENT_SIGNER_CARD}>
			<ExpandablePersonCard
				name={fullName}
				email={email}
				status={personStatus}
				tags={tags}
				subtitle={resolvedSubtitle}
				subtitleColor={resolvedSubtitleColor}
				action={action}
				menuItems={[...(claimMenuItems ?? []), ...(menuItems ?? [])]}
				menuTestId={menuTestId}
				expanded={expanded}
				onToggle={() => setExpanded((prev) => !prev)}
				expandable={hasExpandableContent}
			/>

			{expanded && hasWitnesses && (
				<ConnectedChildren $gap="4px">
					{witnesses?.map((witness) => {
						const witnessStatus: PersonStatus | undefined = witness.status
							? {
									label: getPersonStatusText(witness.status, tMipManage),
									color: SignerPanelStatusColorMap[witness.status],
								}
							: undefined;

						return (
							<div style={{ paddingLeft: '8px' }}>
								<SubPersonRow
									key={witness.email}
									label={witness.email}
									subtitle={tMipManage('documents_page.signer.subtitle.witness')}
									status={witnessStatus}
									action={
										onResendWitnessInvitation &&
										witness.signatureId &&
										!isConcludedStatus(witness.status)
											? {
													label: tMipManage(
														'documents_page.signer.resend_witness_button'
													),
													onClick: () => {
														if (!witness.signatureId) {
															logger.error(
																'SignerCard: Cannot resend witness invitation: missing signatureId',
																witness
															);
															return;
														}

														onResendWitnessInvitation(
															witness.signatureId
														);
													},
													loading:
														resendingSignatureId ===
														witness.signatureId,
												}
											: undefined
									}
									menuItems={getWitnessMenuItems?.(witness)}
									menuTestId={`${TEST_ELEMENT_IDS.DOCUMENT_SIGNER_MENU_TRIGGER}-${DocumentSigningRoleType.WITNESS}`}
								/>
							</div>
						);
					})}
				</ConnectedChildren>
			)}
			{expanded && (
				<Flex ml="1rem" direction="column" gap="0.5rem">
					{children}
				</Flex>
			)}
		</Flex>
	);
};
